/**
 * Compute-time weight override.
 *
 * Some products store a *colisage reference* in their weight cell — a packaging
 * count, not kilograms (e.g. oyster bourriches, whose cell holds pieces-per-box).
 * For those, the real per-unit net weight (kg) is kept under the
 * `compute_time_weight_override` key of the product's metadata bag, and weight
 * totals use `quantity × override` instead of the cell.
 *
 * This is the single reader for that key. It is a plain metadata value (no DB
 * column, no derived API field): callers pass whichever metadata bag they hold
 * (`product_metadata` on the REST `ProductResponse`, `productMetadata` on the
 * synced `Product`). Values may be stored as a number or a numeric string;
 * anything non-positive or unparseable reads as `null` (= "no override"), so a
 * stray `0` can never silently zero weight-based totals.
 */
export const COMPUTE_TIME_WEIGHT_OVERRIDE_KEY = "compute_time_weight_override";

export function readComputeTimeWeightOverride(
  metadata: Record<string, unknown> | null | undefined,
): number | null {
  const raw = metadata?.[COMPUTE_TIME_WEIGHT_OVERRIDE_KEY];
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
